import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { UPDATE_HHG_ESTIMATE_FAILURE } from 'modules/authorizations/store/types/houseHoldGoodsTypes';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import { getHhgSegments, updateHhgEstimate } from 'modules/authorizations/store/actions/houseHoldGoods.actions';
import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { makeStyles } from '@material-ui/styles';
import Spinner from 'modules/common/spinner.component';
import { formatDateForInput } from 'utilities/common';

const useStyles = makeStyles(theme => ({
  card: {
    width: '100%',
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  iconRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  dialogIconRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: '40px',
    paddingBottom: '20px'
  },
  cardDisplayContent: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: '10px'
  },
  chip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  chipClickable: {
    cursor: 'pointer'
  },
  iconOverlayImg: {
    position: 'absolute',
    zIndex: '1 !important'
  },
  spacingX: {
    marginRight: theme.spacing(1)
  },
  footer: {
    height: 15,
    marginBottom: theme.spacing(2)
  },
  mb2: {
    marginBottom: '4px',
    marginRight: '16px'
  },
  editModalContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100% !important',
    marginLeft: '0px !important'
  },
  editModalColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100% !important'
  },
  editModalInput: {
    minWidth: '332px'
  },
  dialogContentContainer: {
    margin: '15px',
    paddingLeft: '50px',
    paddingRight: '50px'
  },
  labelText: {
    minWidth: 350,
    marginBottom: 0
  }
}));

const EditCardModal = props => {
  const { open, onSubmit, isLoading, onClose, estimateInfo } = props;
  const classes = useStyles();

  const [orderedDate, setOrderedDate] = useState(estimateInfo ? estimateInfo.orderedDate : null);
  const [hasVendorAccepted, setHasVendorAccepted] = useState(
    estimateInfo && estimateInfo.hasVendorAccepted && estimateInfo.hasVendorAccepted === true ? 'Yes' : 'No'
  );
  const [reasonNotAccepted, setReasonNotAccepted] = useState(estimateInfo ? estimateInfo.reasonNotAccepted : '');
  const [appointmentDate, setAppointmentDate] = useState(estimateInfo ? estimateInfo.appointmentDate : null);
  const [appointmentConfirmed, setAppointmentConfirmed] = useState(estimateInfo ? estimateInfo.appointmentConfirmed : null);
  const [receivedDate, setReceivedDate] = useState(estimateInfo ? estimateInfo.receivedDate : null);
  const [requestDetailsComment, setRequestDetailsComment] = useState(estimateInfo ? estimateInfo.requestDetailsComment : '');

  const [isDirty, setIsDirty] = useState(false);
  let acceptedOptions = ['Yes', 'No'];

  const onSubmitModal = e => {
    e.preventDefault();

    const values = {
      orderedDate,
      hasVendorAccepted,
      reasonNotAccepted,
      appointmentDate,
      appointmentConfirmed,
      receivedDate,
      requestDetailsComment
    };

    onSubmit(values);
  };

  useEffect(() => {
    if (estimateInfo) {
      if (
        (!(orderedDate === 0 && estimateInfo.orderedDate == null) && orderedDate !== estimateInfo.orderedDate) ||
        (!(hasVendorAccepted === 0 && estimateInfo.hasVendorAccepted == null) && hasVendorAccepted !== estimateInfo.hasVendorAccepted) ||
        (!(appointmentDate === 0 && estimateInfo.appointmentDate == null) && appointmentDate !== estimateInfo.appointmentDate) ||
        (!(appointmentConfirmed === 0 && estimateInfo.appointmentConfirmed == null) && appointmentConfirmed !== estimateInfo.appointmentConfirmed) ||
        (!(receivedDate === 0 && estimateInfo.reasonNotAccepted == null) && receivedDate !== estimateInfo.receivedDate)
      ) {
        setIsDirty(true);
      }
    }
  }, [orderedDate, hasVendorAccepted, appointmentDate, appointmentConfirmed, receivedDate, estimateInfo]);

  return (
    <Dialog open={open} fullWidth={true} maxWidth={'lg'}>
      <form onSubmit={onSubmitModal} fullScreen>
        <div className="row justify-space-between align-center">
          <div className={classes.dialogIconRow}>
            <Tooltip title="Estimates" key="Estimates" placement="right">
              <FontAwesomeIcon icon={['fas', 'fa-truck-moving']} size="3x" />
            </Tooltip>
            <Typography variant="h5" sx={{ paddingLeft: '20px' }}>
              Request Details
            </Typography>
          </div>
        </div>
        <div className={classes.dialogContentContainer}>
          <Stack
            sx={{
              width: '100%',
              minWidth: { xs: '300px', sm: '360px', md: '400px', lg: '400px' },
              gap: '1.5rem'
            }}
          >
            <Grid container spacing={{ xs: 2, md: 3, lg: 5 }} sx={{ padding: '30px', paddingTop: '0px' }}>
              <Grid item>
                <div>
                  <TextField
                    key={'orderedDate'}
                    label={'Ordered Date'}
                    name={'orderedDate'}
                    required={false}
                    disabled={false}
                    value={formatDateForInput(orderedDate)}
                    sx={{ maxWidth: '360px' }}
                    margin="dense"
                    variant="standard"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    onChange={e => setOrderedDate(e.target.value)}
                  />
                </div>
              </Grid>
              <Grid item>
                <div>
                  <FormControl sx={{ marginTop: '6px' }}>
                    <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={'Service Type'}>
                      Vendor Accepted
                    </InputLabel>
                    <Select
                      labelId={'hasVendorAccepted'}
                      key={'hasVendorAccepted'}
                      label={'Vendor Accepted'}
                      name={'hasVendorAccepted'}
                      required={false}
                      disabled={false}
                      value={hasVendorAccepted}
                      margin="dense"
                      variant="standard"
                      sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                      onChange={e => setHasVendorAccepted(e.target.value)}
                    >
                      {acceptedOptions.map(opt => (
                        <MenuItem key={opt} value={opt} sx={{ margin: 'dense' }}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </Grid>
              {hasVendorAccepted && hasVendorAccepted === 'No' && (
                <>
                  <Grid item>
                    <div>
                      <TextField
                        key={'reasonNotAccepted'}
                        label={'Reason Not Accepted'}
                        name={'reasonNotAccepted'}
                        required={false}
                        disabled={false}
                        value={reasonNotAccepted}
                        sx={{ maxWidth: '360px' }}
                        margin="dense"
                        variant="standard"
                        type="text"
                        onChange={e => setReasonNotAccepted(e.target.value)}
                      />
                    </div>
                  </Grid>
                </>
              )}

              <Grid item>
                <div>
                  <TextField
                    key={'appointmentDate'}
                    label={'Appointment Date'}
                    name={'appointmentDate'}
                    required={false}
                    disabled={false}
                    value={formatDateForInput(appointmentDate)}
                    sx={{ maxWidth: '360px' }}
                    margin="dense"
                    variant="standard"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    onChange={e => setAppointmentDate(e.target.value)}
                  />
                </div>
              </Grid>
              <Grid item>
                <div>
                  <TextField
                    key={'appointmentConfirmed'}
                    label={'Appointment Confirmed'}
                    name={'appointmentConfirmed'}
                    required={false}
                    disabled={false}
                    value={formatDateForInput(appointmentConfirmed)}
                    sx={{ maxWidth: '360px' }}
                    margin="dense"
                    variant="standard"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    onChange={e => setAppointmentConfirmed(e.target.value)}
                  />
                </div>
              </Grid>
              <Grid item>
                <div>
                  <TextField
                    key={'receivedDate'}
                    label={'Received Date'}
                    name={'receivedDate'}
                    required={false}
                    disabled={false}
                    value={formatDateForInput(receivedDate)}
                    sx={{ maxWidth: '360px' }}
                    margin="dense"
                    variant="standard"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    onChange={e => setReceivedDate(e.target.value)}
                  />
                </div>
              </Grid>
              <Grid item>
                <div>
                  <TextField
                    key={'requestDetailsComment'}
                    label={'Comment'}
                    name={'requestDetailsComment'}
                    required={false}
                    disabled={false}
                    value={requestDetailsComment}
                    multiline
                    sx={{ width: '1000px' }}
                    margin="dense"
                    variant="standard"
                    type="text"
                    onChange={e => setRequestDetailsComment(e.target.value)}
                  />
                </div>
              </Grid>
              {/* <Box sx={{ width: '100%', marginTop: '20px' }}>
                    <hr />
                  </Box>
                  <Grid item >
                      <div>
                        <TextField
                          key={'vendorContactName'}
                          label={'Vendor Contact Name'}
                          name={'vendorContactName'}
                          required={false}
                          disabled={true}
                          value={vendorContactName}
                          sx={{ maxWidth: '360px' }}
                          margin="dense"
                          variant="standard"
                          type="text"
                          onChange={(e) => setVendorContactName(e.target.value)}
                        />
                      </div>
                    </Grid>
                    <Grid item >
                      <div>
                        <TextField
                          key={'vendorContactPhone'}
                          label={'Vendor Contact Phone'}
                          name={'vendorContactPhone'}
                          required={false}
                          disabled={true}
                          value={vendorContactPhone}
                          sx={{ maxWidth: '360px' }}
                          margin="dense"
                          variant="standard"
                          type="text"
                          onChange={(e) => setVendorContactPhone(e.target.value)}
                        />
                      </div>
                    </Grid>
                    <Grid item >
                      <div>
                        <TextField
                          key={'vendorContactEmail'}
                          label={'Vendor Contact Email'}
                          name={'vendorContactEmail'}
                          required={false}
                          disabled={true}
                          value={vendorContactEmail}
                          sx={{ maxWidth: '360px' }}
                          margin="dense"
                          variant="standard"
                          type="text"
                          onChange={(e) => setVendorContactEmail(e.target.value)}
                        />
                      </div>
                    </Grid> */}
            </Grid>
          </Stack>
        </div>
        {isLoading && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Spinner />
          </DialogActions>
        )}
        {!isLoading && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button color="primary" type="submit" variant="contained" disabled={!isDirty}>
              Save
            </Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

const RequestDetailsEstimatesCard = props => {
  const [isLoading, setIsLoading] = useState(false);
  const [editingModalOpen, setEditingModalOpen] = useState(false);

  const classes = useStyles();

  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  const { estimate, hideEdit, authId } = props;
  const { estimateInfo } = estimate;

  const safeDate = date => {
    if (!date || date === '' || date === undefined) return null;
    return date;
  };

  const handleSaveEdits = async values => {
    setIsLoading(true);

    let newVals = { ...estimateInfo };
    newVals.orderedDate = safeDate(values.orderedDate);
    newVals.reasonNotAccepted = values.reasonNotAccepted;
    newVals.appointmentDate = safeDate(values.appointmentDate);
    newVals.appointmentConfirmed = safeDate(values.appointmentConfirmed);
    newVals.receivedDate = safeDate(values.receivedDate);
    newVals.requestDetailsComment = values.requestDetailsComment;

    if (values.hasVendorAccepted === 'No') {
      newVals.hasVendorAccepted = false;
    }
    if (values.hasVendorAccepted === 'Yes') {
      newVals.hasVendorAccepted = true;
    }

    let newRequestBody = { ...estimate };
    newRequestBody.estimateInfo = newVals;

    const resp = await props.updateHhgEstimate(authId, newRequestBody);
    if (resp.type === UPDATE_HHG_ESTIMATE_FAILURE) {
      props.showToast('Failed to update the estimate info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoading(false);
      return false;
    } else {
      await props.getHhgSegments(authId);
      setIsLoading(false);
      setEditingModalOpen(false);
      return true;
    }
  };

  useEffect(() => {}, [estimateInfo]);

  return (
    <ThemeProvider theme={theme}>
      <Card className={classes.card}>
        <Box>
          <CardContent className={classes.cardContainer}>
            <div className="row justify-space-between align-center">
              <div className={classes.iconRow}>
                <Tooltip title="Estimates" key="Estimates" placement="right">
                  <FontAwesomeIcon icon={['fas', 'money-check-dollar']} size="2x" />
                </Tooltip>
                <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                  Request Details
                </Typography>
              </div>
              {!hideEdit && (
                <>
                  <Fab className={classes.iconOverlayImg} color="primary" size="small" onClick={() => setEditingModalOpen(true)}>
                    <FontAwesomeIcon size="1x" icon={['fas', 'pen']} color={theme.palette.common.white} />
                  </Fab>
                </>
              )}
            </div>
          </CardContent>
          <CardContent sx={{ padding: '10px', marginLeft: '30px' }}>
            <Grid container spacing={2} columns={{ xs: 4, sm: 4, md: 4, xl: 4 }}>
              <Grid item xs={2} sm={2} md={1} xl={1}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Ordered Date
                    </Typography>
                  </div>
                  <div className={classes.mb2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                      {estimateInfo && estimateInfo.orderedDate ? (
                        new Date(estimateInfo.orderedDate).toLocaleDateString('en-us')
                      ) : (
                        <div>&mdash;</div>
                      )}
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item xs={2} sm={2} md={1} xl={1}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Vendor Accepted
                    </Typography>
                  </div>
                  <div className={classes.mb2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                      {estimateInfo && estimateInfo.hasVendorAccepted && estimateInfo.hasVendorAccepted === true ? 'Yes' : 'No'}
                    </Typography>
                  </div>
                </Box>
              </Grid>
              {estimateInfo && !estimateInfo.hasVendorAccepted && (
                <>
                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Reason Not Accepted
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {estimateInfo && estimateInfo.reasonNotAccepted ? estimateInfo.reasonNotAccepted : <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>
                </>
              )}

              <Grid item xs={2} sm={2} md={1} xl={1}>
                <Box>
                  <div sx={{ marginLeft: '-50px', marginTop: '40px' }}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Appointment Date
                    </Typography>
                  </div>
                  <div className={classes.mb2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                      {estimateInfo && estimateInfo.appointmentDate ? (
                        new Date(estimateInfo.appointmentDate).toLocaleDateString('en-us')
                      ) : (
                        <div>&mdash;</div>
                      )}
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item xs={2} sm={2} md={1} xl={1}>
                <Box>
                  <div sx={{ marginLeft: '-50px', marginTop: '40px' }}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Appointment Confirmed
                    </Typography>
                  </div>
                  <div className={classes.mb2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                      {estimateInfo && estimateInfo.appointmentConfirmed ? (
                        new Date(estimateInfo.appointmentConfirmed).toLocaleDateString('en-us')
                      ) : (
                        <div>&mdash;</div>
                      )}
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item xs={2} sm={2} md={1} xl={1}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Received Date
                    </Typography>
                  </div>
                  <div className={classes.mb2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                      {estimateInfo && estimateInfo.receivedDate ? (
                        new Date(estimateInfo.receivedDate).toLocaleDateString('en-us')
                      ) : (
                        <div>&mdash;</div>
                      )}
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item xs={2} sm={2} md={1} xl={1}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Comment
                    </Typography>
                  </div>
                  <div className={classes.mb2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                      {estimateInfo && estimateInfo.requestDetailsComment ? estimateInfo.requestDetailsComment : <div>&mdash;</div>}
                    </Typography>
                  </div>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Box>
      </Card>
      {editingModalOpen && (
        <EditCardModal
          classes={classes}
          tab={0}
          open={editingModalOpen}
          isLoading={isLoading}
          onClose={() => setEditingModalOpen(false)}
          onSubmit={handleSaveEdits}
          estimateInfo={estimateInfo}
        />
      )}
    </ThemeProvider>
  );
};

export default compose(
  connect(null, {
    updateHhgEstimate,
    showToast,
    getHhgSegments
  })
)(RequestDetailsEstimatesCard);
